import React, { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { Box, Typography, Link } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../data/store';
import * as GoogleStorageService from '../../../services/google-storage.service';
import { processImageForUpload } from '../../../utils/image-processing';
import { updateClient } from '../../../services/client-service';
import { showAlert } from '../../../data/stores/alertStore';
import {
	asyncCallDone,
	asyncCallError,
	asyncCallStart,
	refreshClientSuccess,
} from '../../../data/stores/clientStore';
import Modal from '../../common/Modal';
import ModalHeader from '../../common/ModalHeader';
import SaveCancelButtons from '../../common/buttons/SaveCancelButtons';
import UploadFileIcon from '@mui/icons-material/UploadFile';

interface ClientLogoUploadModalProps {
	open: boolean;
	onClose: () => void;
	clientId: string;
	existingLogoUrl?: string;
}

export default function ClientLogoUploadModal({
	open,
	onClose,
	clientId,
	existingLogoUrl,
}: ClientLogoUploadModalProps) {
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(
		existingLogoUrl || null
	);
	const dispatch = useDispatch<AppDispatch>();
	const companyId = useSelector(
		(state: RootState) => state.companyState?.company?.id
	);

	const onDrop = useCallback((acceptedFiles: File[]) => {
		const file = acceptedFiles[0];
		if (!file) return;

		setSelectedFile(file);
		setPreviewUrl(URL.createObjectURL(file));
	}, []);

	const handleUpload = async () => {
		if (!selectedFile) return;

		try {
			dispatch(asyncCallStart());

			// Process the image (convert HEIC/HEIF and resize if needed)
			const { processedFile } = await processImageForUpload(selectedFile);

			// Upload file to backend
			// add company id
			const path = `${companyId}/client/${clientId}/logo/${processedFile.name}`;
			const response = await GoogleStorageService.uploadFile(
				processedFile,
				path
			);

			// Update client with new logo URL
			const updatedClient = await updateClient({
				id: clientId,
				logoUrl: response.url,
			});

			dispatch(refreshClientSuccess(updatedClient));
			dispatch(
				showAlert({
					message: 'Logo uploaded successfully',
					severity: 'success',
				})
			);
			onClose();
		} catch (error) {
			console.error('Error uploading logo:', error);
			dispatch(
				showAlert({
					message: 'Error uploading logo',
					severity: 'error',
				})
			);
		} finally {
			dispatch(asyncCallDone());
		}
	};

	const { getRootProps, getInputProps, isDragActive } = useDropzone({
		onDrop,
		accept: {
			'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.svg'],
		},
		maxFiles: 1,
		maxSize: 5 * 1024 * 1024, // 5MB
	});

	return (
		<Modal open={open} handleClose={onClose}>
			<Box sx={{ minWidth: 400, p: 2 }}>
				<ModalHeader>Add Company Logo</ModalHeader>
				<DropZone {...getRootProps()}>
					<input {...getInputProps()} />
					<StyledUploadIcon />
					<UploadText variant="body1">
						<Link component="span">Click to upload</Link> or drag
						and drop
					</UploadText>
					<SubText variant="body2">
						SVG, PNG or JPG (max. 5MB)
					</SubText>
				</DropZone>

				{previewUrl && (
					<PreviewContainer>
						<LogoPreview src={previewUrl} alt="Logo Preview" />
						<Typography
							variant="body2"
							color="textSecondary"
							sx={{ mt: 1 }}
						>
							{selectedFile?.name || 'Current logo'}
						</Typography>
					</PreviewContainer>
				)}

				<Box sx={{ mt: 3 }}>
					<SaveCancelButtons
						handleSave={handleUpload}
						handleCancel={onClose}
						saveText="UPLOAD"
						saveDisabled={!selectedFile}
					/>
				</Box>
			</Box>
		</Modal>
	);
}

const DropZone = styled(Box)(({ theme }) => ({
	border: `2px dashed ${
		theme.palette.mode === 'dark' ? '#404040' : '#e0e0e0'
	}`,
	borderRadius: theme.shape.borderRadius,
	textAlign: 'center',
	cursor: 'pointer',
	minHeight: '200px',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'center',
	justifyContent: 'center',
	gap: theme.spacing(2),
	'&:hover': {
		borderColor: theme.palette.primary.main,
		backgroundColor: theme.palette.mode === 'dark' ? '#202020' : '#f0f0f0',
	},
}));

const StyledUploadIcon = styled(UploadFileIcon)(({ theme }) => ({
	fontSize: 48,
	color: theme.palette.primary.main,
	marginBottom: theme.spacing(2),
}));

const UploadText = styled(Typography)(({ theme }) => ({
	display: 'flex',
	alignItems: 'center',
	gap: theme.spacing(1),
	color: theme.palette.text.secondary,
	'& .MuiLink-root': {
		cursor: 'pointer',
		color: theme.palette.primary.main,
		textDecoration: 'none',
		'&:hover': {
			textDecoration: 'underline',
		},
	},
}));

const SubText = styled(Typography)(({ theme }) => ({
	color: theme.palette.text.secondary,
	marginTop: theme.spacing(1),
}));

const LogoPreview = styled('img')(({ theme }) => ({
	maxWidth: '200px',
	maxHeight: '200px',
	objectFit: 'contain',
	borderRadius: theme.shape.borderRadius,
	marginTop: theme.spacing(2),
}));

const PreviewContainer = styled(Box)(({ theme }) => ({
	marginTop: theme.spacing(2),
	textAlign: 'center',
}));
